import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator as CalcIcon, ShoppingCart, Square as RectIcon, Circle as CircleIcon, Triangle as TriIcon, Shapes as IrregIcon } from 'lucide-react';
import { useCart } from './CartContext';
import { Link } from 'react-router-dom';
export type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
export const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
export const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$67'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$44'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$73'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$87'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$76'
}, {
  id: '34-clean',
  name: '3/4" Crushed Clear',
  density: 1.35,
  priceRange: '$89'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$76'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$60'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}];
type MaterialCalculatorProps = {
  variant?: 'modal' | 'inline' | 'page';
  initialMaterial?: string;
  initialProjectType?: string;
  onAddToCart?: (yards: number, material: string) => void;
};
export function MaterialCalculator({
  variant = 'inline',
  initialMaterial = 'crushed-rock',
  initialProjectType = 'driveway',
  onAddToCart
}: MaterialCalculatorProps) {
  const {
    addToCart,
    openCart
  } = useCart();
  const [projectType, setProjectType] = useState(initialProjectType);
  const [material, setMaterial] = useState(calcMaterials.find(m => m.id === initialMaterial) ? initialMaterial : 'crushed-rock');
  const [shape, setShape] = useState<Shape>('rectangle');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState(() => {
    const pt = projectTypes.find(p => p.id === initialProjectType);
    return pt ? pt.defaultDepth.toString() : '4';
  });
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  const handleProjectTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as typeof projectType;
    setProjectType(newType);
    // Reset dependent fields if needed (placeholder logic)
    setCalcResults(null);
  };
  // Clear stale results when shape changes
  useEffect(() => {
    setCalcResults(null);
  }, [shape]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert all linear dimensions to feet (input is ft or m).
    const toFeet = (val: string) => {
      const n = parseFloat(val);
      if (isNaN(n)) return NaN;
      return unit === 'm' ? n * 3.28084 : n;
    };
    // Compute the project area (sq ft) from the active shape's inputs.
    let area = NaN;
    if (shape === 'rectangle' || shape === 'irregular') {
      const l = toFeet(length);
      const w = toFeet(width);
      if (!isNaN(l) && !isNaN(w)) area = l * w;
    } else if (shape === 'circle') {
      const d = toFeet(diameter);
      if (!isNaN(d)) area = Math.PI * (d / 2) ** 2;
    } else if (shape === 'triangle') {
      const b = toFeet(base);
      const h = toFeet(height);
      if (!isNaN(b) && !isNaN(h)) area = 0.5 * b * h;
    }
    // Depth is entered in inches or cm — normalize to feet.
    const depthVal = parseFloat(depth);
    if (isNaN(area) || isNaN(depthVal) || area <= 0 || depthVal <= 0) {
      setCalcResults(null);
      return;
    }
    const depthInches = depthUnit === 'cm' ? depthVal / 2.54 : depthVal;
    const depthFeet = depthInches / 12;
    const cubicFeet = area * depthFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleAddToCartClick = () => {
    if (!calcResults) return;
    const roundedYards = Math.ceil(calcResults.yards);
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    if (onAddToCart) {
      onAddToCart(roundedYards, selectedMaterial.name);
    } else {
      // Dynamically load the splitter utility and add each line item to the cart.
      import('../utils/loadSplit').then(({
        buildDeliveryLineItems
      }) => {
        const lineItems = buildDeliveryLineItems(roundedYards, selectedMaterial.name);
        lineItems.forEach(li => {
          addToCart({
            category: 'Calculated',
            product: li.product,
            quantity: li.quantity,
            pricePerYard: parseFloat(selectedMaterial.priceRange)
          });
        });
      });
    }
    openCart();
  };
  // CSS class constants based on the variant prop
  const inputClass = 'w-full px-4 py-3 bg-white dark:bg-[#1a1a1a] border border-[#e5e5e5] dark:border-[#333] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all';
  const containerClass = variant === 'page' ? 'w-full' : variant === 'modal' ? 'w-full' : 'bg-[#f5f5f5] dark:bg-[#2a2a2a] rounded-2xl border border-[#e5e5e5] dark:border-white/10 shadow-xl overflow-hidden';
  const paddingClass = variant === 'page' ? '' : 'p-6 sm:p-8';
  return <div className={containerClass} data-id="element-1209" data-mp="3qinjtye1oj75">
      <div className={containerClass} data-id="element-1210" data-mp="3qjq9y43jmhm0">
        <div className={`grid grid-cols-1 ${variant === 'page' ? 'lg:grid-cols-3 gap-8' : 'lg:grid-cols-5'}`} data-id="element-1211" data-mp="35mhd1a8evi8r">
          <div className={`${paddingClass} ${variant === 'page' ? 'lg:col-span-2 space-y-6' : 'lg:col-span-3 border-b lg:border-b-0 lg:border-r border-[#e5e5e5] dark:border-[#555555]'}`} data-id="element-1212" data-mp="39m8299iahqb6">
            {/* Shape Tabs. Icon-led layout — each tab shows the shape
             glyph with a small label below. Reads instantly even in
             narrow embeds (Product page sidebar) where text-only tabs
             would truncate to a single letter. */}
            <div className="dark:bg-[#222] p-1.5 rounded-2xl border border-[#e5e5e5] dark:border-[#333] flex flex-nowrap gap-1 mb-6 bg-[#eeeeee]" data-id="element-1213" data-mp="2vni37obwtgk6">
              {[{
              id: 'rectangle' as Shape,
              Icon: RectIcon,
              label: 'Rectangle'
            }, {
              id: 'circle' as Shape,
              Icon: CircleIcon,
              label: 'Circle'
            }, {
              id: 'triangle' as Shape,
              Icon: TriIcon,
              label: 'Triangle'
            }, {
              id: 'irregular' as Shape,
              Icon: IrregIcon,
              label: 'Irregular'
            }].map(({
              id,
              Icon,
              label
            }) => <button key={id} type="button" onClick={() => setShape(id)} aria-label={label} className={`relative flex-1 min-w-0 py-2 px-1 rounded-xl transition-colors z-10 flex flex-col items-center justify-center gap-1 ${shape === id ? 'text-white' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`} data-id="element-1214" data-mp="1lpifjzbj9ej8">
                  {shape === id && <motion.div layoutId="shapeTab" className="absolute inset-0 bg-[#3a3a3a] dark:bg-[#444] rounded-xl -z-10" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-1215" data-mp="17pzh5y8ixhk4" />}
                  <Icon className="w-5 h-5" strokeWidth={shape === id ? 2.25 : 1.75} data-id="element-1216" data-mp="2x45nyc8kb8xk" />
                  <span className="text-[10px] font-semibold leading-none tracking-wide" data-id="element-1217" data-mp="pclkm63jo1x3">
                    {label}
                  </span>
                </button>)}
            </div>

            <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1218" data-mp="kfv118sj6o6x">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1219" data-mp="za9ekttbt5e7">
                <div data-id="element-1220" data-mp="ubexbwb0bow7">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1221" data-mp="uc7uq6o2t21o">
                    Project Type
                  </label>
                  <select value={projectType} onChange={handleProjectTypeChange} className={`${inputClass} bg-white`} data-id="element-1222" data-mp="1o28tglad709">
                    {projectTypes.map(pt => <option key={pt.id} value={pt.id} data-id="element-1223" data-mp="gdqc2nd12fqm">
                        {pt.name}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-1224" data-mp="3jzfjacamf73f">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1225" data-mp="3jyw06s98icnt">
                    Material
                  </label>
                  <select value={material} onChange={e => setMaterial(e.target.value)} className={`${inputClass} bg-white`} data-id="element-1226" data-mp="1uia60llpk62b">
                    {calcMaterials.map(m => <option key={m.id} value={m.id} data-id="element-1227" data-mp="1km785rxw2tl6">
                        {m.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-1228" data-mp="2dacrqbd7icjf">
                <div className="flex dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333] bg-[#eeeeee]" data-id="element-1229" data-mp="2dbnty6r07c8f">
                  <button type="button" onClick={() => setUnit('ft')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'ft' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1230" data-mp="2i7cx0k28fb9h">
                    Feet
                  </button>
                  <button type="button" onClick={() => setUnit('m')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1231" data-mp="st7urdd1vdex">
                    Meters
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1232" data-mp="2um19ca77jlg6">
                <AnimatePresence mode="wait" data-id="element-1233" data-mp="2prdvd59ty9fu">
                  {(shape === 'rectangle' || shape === 'irregular') && <motion.div key="rect" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1234" data-mp="2ktc9kcl0rw0o">
                      <div data-id="element-1235" data-mp="21vt23de7mlgd">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1236" data-mp="21wkktnmpkop8">
                          Length ({unit})
                        </label>
                        <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1237" data-mp="4fuuhors1h5" />
                      </div>
                      <div data-id="element-1238" data-mp="ck5w9g7liyok">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1239" data-mp="hd8mbed5zq00">
                          Width ({unit})
                        </label>
                        <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={`${inputClass} bg-white`} required min="0.1" step="any" data-id="element-1240" data-mp="3lroxdk8bf0ao" />
                      </div>
                    </motion.div>}
                  {shape === 'circle' && <motion.div key="circ" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2" data-id="element-1241" data-mp="3uu81e7nu7iyi">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1242" data-mp="2nx5wuvt93fia">
                        Diameter ({unit})
                      </label>
                      <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1243" data-mp="2wyvp23uyiih4" />
                    </motion.div>}
                  {shape === 'triangle' && <motion.div key="tri" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1244" data-mp="36uyyzvw1vlem">
                      <div data-id="element-1245" data-mp="3pm89o8hk4g7s">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1246" data-mp="3pnjd2773blyd">
                          Base ({unit})
                        </label>
                        <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1247" data-mp="84vmox0kma46" />
                      </div>
                      <div data-id="element-1248" data-mp="3ub0xnheylwfj">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1249" data-mp="3udwv74mwaj3g">
                          Height ({unit})
                        </label>
                        <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputClass} required min="0.1" step="any" data-id="element-1250" data-mp="3q9yw2txkl8ae" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <div className="col-span-1 sm:col-span-2 border-t border-[#e5e5e5] dark:border-[#333] pt-6 mt-2" data-id="element-1251" data-mp="3gknnr9iic7nk">
                  <div className="flex justify-between items-end mb-2" data-id="element-1252" data-mp="32mhc3kkjrehz">
                    <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider" data-id="element-1253" data-mp="36kojvpx195h1">
                      Depth
                    </label>
                    <div className="flex bg-[#eeeeee] dark:bg-[#1a1a1a] rounded-lg p-1 border border-[#e5e5e5] dark:border-[#333]" data-id="element-1254" data-mp="18c97zti8ri4d">
                      <button type="button" onClick={() => setDepthUnit('in')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'in' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1255" data-mp="13dpi5i0j1m6q">
                        Inches
                      </button>
                      <button type="button" onClick={() => setDepthUnit('cm')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'cm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1256" data-mp="1r3tnpux9zw0d">
                        cm
                      </button>
                    </div>
                  </div>
                  <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className={`${inputClass} bg-white`} placeholder="0" required min="0.1" step="any" data-id="element-1257" data-mp="6mnh5b7eod64" />
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center" data-id="element-1258" data-mp="2md5g7erpxqbw">
                <CalcIcon className="w-5 h-5 mr-2" data-id="element-1259" data-mp="2w2719jd4exwq" />
                Calculate Yardage
              </button>
            </form>
          </div>

          <div className={`${paddingClass} ${variant === 'page' ? 'lg:col-span-1' : 'lg:col-span-2'} bg-[#eeeeee] dark:bg-[#303030] flex flex-col justify-center border-l-4 border-[#CB6E0A] dark:border-[#F6A93B]`} data-id="element-1260" data-mp="3134fjkv0f7sy">
            {calcResults ? <div className="space-y-6" data-id="element-1261" data-mp="3f0j9n2w7k3r9">
                <div data-id="element-1262" data-mp="3ezhzd0n0nlal">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1263" data-mp="3eyoy2f6ttnzu">
                    Estimated Volume
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-1264" data-mp="1knjpqxksone6">
                    <span className="font-heading font-bold text-4xl text-stone-900 dark:text-white" data-id="element-1265" data-mp="3mjshgrp2fue7">
                      {calcResults.yards}
                    </span>
                    <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1266" data-mp="3igw53iq4cuip">
                      Cubic Yards
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1267" data-mp="3rd3pux9j0j88">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1268" data-mp="33lpyatj78rob">
                    Estimated Weight
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-1269" data-mp="3dkf7lxx153sy">
                    <span className="font-heading font-bold text-3xl text-stone-900 dark:text-white" data-id="element-1270" data-mp="38ksstk1jzfi1">
                      {calcResults.tons}
                    </span>
                    <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1271" data-mp="svhoqwtu5hr2">
                      Tons
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#e5e5e5] dark:border-[#4b4b4b]" data-id="element-1272" data-mp="25qlxoeapgqgj">
                  <p className="text-sm font-bold text-[#CB6E0A] dark:text-[#F6A93B] uppercase tracking-wider mb-1" data-id="element-1273" data-mp="2asl2ztq09xg4">
                    Material Price Range
                  </p>
                  <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1274" data-mp="21rd69g3514o5">
                    {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                    <span className="text-sm text-stone-500 font-normal" data-id="element-1275" data-mp="2th9l67x3too7">
                      / yard
                    </span>
                  </p>
                </div>

                {variant === 'page' ? <Link to="/#materials" className="w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center bg-brand-orange hover:bg-[#e66a2c] text-white shadow-lg shadow-brand-orange/20 mt-6" data-id="element-1276" data-mp="2kp7lwcyruefi">
                    <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1277" data-mp="3d60dvqscev1f" />
                    Shop Materials
                  </Link> : <button type="button" onClick={handleAddToCartClick} className="w-full mt-6 py-3.5 bg-brand-charcoal dark:bg-white text-white dark:text-stone-900 font-bold rounded-xl hover:bg-stone-800 dark:hover:bg-stone-100 transition-colors flex items-center justify-center" data-id="element-1278" data-mp="3tepv5ijzlp34">
                    <ShoppingCart className="w-4 h-4 mr-2" data-id="element-1279" data-mp="3oo16eazoz67o" />
                    {onAddToCart ? `Apply ${Math.ceil(calcResults.yards)} Yards` : `Add ${Math.ceil(calcResults.yards)} Yards to Cart`}
                  </button>}
              </div> : <div className="text-center text-stone-500 dark:text-stone-400" data-id="element-1280" data-mp="3fomdz5zyslkj">
                <CalcIcon className="w-12 h-12 mx-auto mb-4 opacity-20 text-[#CB6E0A] dark:text-[#F6A93B]" data-id="element-1281" data-mp="31obmc0qtz2yb" />
                <p data-id="element-1282" data-mp="35on9plakvhdh">Enter your dimensions to see estimated volume and weight.</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}